'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import StepIndicator from './StepIndicator';
import StepA from './StepA';
import StepB from './StepB';
import StepC from './StepC';
import StepD from './StepD';
import StepE from './StepE';
import StepF from './StepF';
import StepG from './StepG';
import OrderSummaryPanel from './OrderSummaryPanel';
import { useAuth } from '@/contexts/AuthContext';

export type OrderState = {
  market: 'florida' | 'puerto-rico';
  issue: string;
  issueLabel: string;
  issuePrintDate: string;
  issueArtworkDeadline: string;
  regions: string[];
  placement: string;
  placementName: string;
  placementBasePrice: number; // live price per region per issue from DB
  term: 'one' | 'three';
  addDesign: boolean;
  artworkFile: File | null;
  artworkType: 'upload' | 'design-service' | null;
  designBrief: string;
  agreementChecks: { perRegion: boolean; deadlines: boolean; noRefund: boolean; chargeback: boolean };
  signatureName: string;
  signatureDate: string;
  paymentComplete: boolean;
  // Coupon fields — set by StepG when a coupon is applied
  couponCode: string | null;
  couponDiscount: number;
  couponFinalTotal: number | null;
};

const STEPS = [
  { id: 'a', label: 'Market & Issue', short: 'Market' },
  { id: 'b', label: 'Select Regions', short: 'Regions' },
  { id: 'c', label: 'Placement & Term', short: 'Placement' },
  { id: 'd', label: 'Add-ons', short: 'Add-ons' },
  { id: 'e', label: 'Artwork', short: 'Artwork' },
  { id: 'f', label: 'Agreement', short: 'Agreement' },
  { id: 'g', label: 'Payment', short: 'Payment' },
];

const initialOrder: OrderState = {
  market: 'florida',
  issue: '',
  issueLabel: '',
  issuePrintDate: '',
  issueArtworkDeadline: '',
  regions: [],
  placement: 'standard',
  placementName: '',
  placementBasePrice: 0,
  term: 'one',
  addDesign: false,
  artworkFile: null,
  artworkType: null,
  designBrief: '',
  agreementChecks: { perRegion: false, deadlines: false, noRefund: false, chargeback: false },
  signatureName: '',
  signatureDate: '',
  paymentComplete: false,
  couponCode: null,
  couponDiscount: 0,
  couponFinalTotal: null,
};

export default function OrderWizardContent() {
  const [step, setStep] = useState(0);
  const [order, setOrder] = useState<OrderState>(initialOrder);
  const [showErrors, setShowErrors] = useState(false);
  const { user } = useAuth();

  // Track whether we've already recorded an abandonment for the current session
  const abandonmentTrackedRef = useRef(false);
  // Track the highest step reached
  const maxStepReachedRef = useRef(0);

  const updateOrder = (updates: Partial<OrderState>) => {
    setOrder(prev => ({ ...prev, ...updates }));
  };

  const goNext = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
  };

  const goBack = () => {
    if (step > 0) {
      setShowErrors(false);
      setStep(s => s - 1);
    }
  };

  const goToStep = (index: number) => {
    if (index < step) {
      setShowErrors(false);
      setStep(index);
    }
  };

  // Track max step reached
  useEffect(() => {
    if (step > maxStepReachedRef.current) {
      maxStepReachedRef.current = step;
    }
  }, [step]);

  // Reset abandonment tracking when user re-enters a high step
  useEffect(() => {
    if (step >= 4) {
      abandonmentTrackedRef.current = false;
    }
  }, [step]);

  /**
   * Record an abandoned checkout session.
   * Called on page unload / visibility change when step >= 4 (Artwork step, 0-based).
   * Step 4 = Artwork, Step 5 = Agreement, Step 6 = Payment
   */
  const recordAbandonment = useCallback(async (currentStep: number, currentOrder: OrderState) => {
    // Only track if user reached step 4 (Artwork) or higher and hasn't paid
    if (currentStep < 4 || currentOrder.paymentComplete) return;
    // Don't double-track
    if (abandonmentTrackedRef.current) return;

    abandonmentTrackedRef.current = true;

    // Calculate total for the email summary
    const baseRate = currentOrder.placementBasePrice ?? 0;
    const getDiscount = (count: number) => {
      if (currentOrder.placement !== 'standard') return 0;
      if (count >= 8) return 0.15;
      if (count >= 5) return 0.10;
      if (count >= 3) return 0.05;
      return 0;
    };
    const discount = getDiscount(currentOrder.regions.length);
    const discountedRate = Math.round(baseRate * (1 - discount));
    const subtotal = discountedRate * currentOrder.regions.length;
    const designFee = currentOrder.addDesign ? 150 : 0;
    const rawTotal = subtotal + designFee;
    const finalTotal = currentOrder.couponFinalTotal !== null ? currentOrder.couponFinalTotal : rawTotal;

    const payload = {
      userId: user?.id || null,
      email: user?.email || null,
      fullName: user?.user_metadata?.full_name || user?.email || null,
      market: currentOrder.market,
      issueLabel: currentOrder.issueLabel || currentOrder.issue,
      regions: currentOrder.regions,
      placementName: currentOrder.placementName,
      term: currentOrder.term,
      addDesign: currentOrder.addDesign,
      totalAmount: finalTotal > 0 ? finalTotal : null,
      couponCode: currentOrder.couponCode,
      couponDiscount: currentOrder.couponDiscount > 0 ? currentOrder.couponDiscount : null,
      stepReached: currentStep,
      checkoutUrl: `${window.location.origin}/order-wizard`,
    };

    // Skip if no email (unauthenticated user with no email)
    if (!payload.email) return;

    try {
      await fetch('/api/orders/track-abandoned-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        // Use keepalive so the request survives page unload
        keepalive: true,
      });
    } catch {
      // Silent — abandonment tracking must never disrupt the user experience
    }
  }, [user]);

  /**
   * Mark the session as completed when payment succeeds.
   * Called by StepG's onComplete callback.
   */
  const markCheckoutComplete = useCallback(async () => {
    if (!user?.email) return;
    try {
      await fetch('/api/orders/track-abandoned-checkout', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
        keepalive: true,
      });
    } catch {
      // Silent
    }
  }, [user]);

  // Track abandonment on page unload (browser close / navigation away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      recordAbandonment(maxStepReachedRef.current, order);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        recordAbandonment(maxStepReachedRef.current, order);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [order, recordAbandonment]);

  const canProceedFromStep = (s: number): boolean => {
    switch (s) {
      case 0: return !!order.market && !!order.issue;
      case 1: return order.regions.length > 0;
      case 2: return !!order.placement && !!order.term;
      case 3: return true;
      case 4: {
        if (!order.artworkType) return false;
        if (order.artworkType === 'upload') return !!order.artworkFile;
        if (order.artworkType === 'design-service') {
          const brief = order.designBrief || '';
          return brief.includes('Business:') && brief.includes('Offer:');
        }
        return false;
      }
      case 5: return (
        order.agreementChecks.perRegion &&
        order.agreementChecks.deadlines &&
        order.agreementChecks.noRefund &&
        order.agreementChecks.chargeback &&
        order.signatureName.trim().length > 2
      );
      case 6: return true;
      default: return true;
    }
  };

  const stepComponents = [
    <StepA key="step-a" order={order} updateOrder={updateOrder} showErrors={showErrors} />,
    <StepB key="step-b" order={order} updateOrder={updateOrder} showErrors={showErrors} />,
    <StepC key="step-c" order={order} updateOrder={updateOrder} showErrors={showErrors} />,
    <StepD key="step-d" order={order} updateOrder={updateOrder} showErrors={showErrors} />,
    <StepE key="step-e" order={order} updateOrder={updateOrder} showErrors={showErrors} />,
    <StepF key="step-f" order={order} updateOrder={updateOrder} showErrors={showErrors} />,
    <StepG key="step-g" order={order} updateOrder={updateOrder} onComplete={() => {
      markCheckoutComplete();
      toast.success('Payment complete! Your order is confirmed.');
    }} />,
  ];

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-6 sm:py-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-800 text-white mb-1">Book Your Ad</h1>
        <p className="text-sm text-slate-400">Complete all steps to reserve your placement in ZipAdBook.</p>
      </div>

      {/* Step indicator */}
      <StepIndicator steps={STEPS} currentStep={step} onStepClick={goToStep} completedSteps={step} />

      <div className="mt-6 sm:mt-8 grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-4 gap-6 sm:gap-8">
        {/* Main wizard content */}
        <div className="xl:col-span-2 2xl:col-span-3 order-1">
          <div className="glass-card rounded-2xl p-4 sm:p-6 md:p-8 border border-white/8 animate-slide-up">
            {stepComponents[step]}

            {/* Navigation */}
            {step < 6 && (
              <div className="flex items-center justify-between mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-white/8 gap-3">
                <button
                  onClick={goBack}
                  disabled={step === 0}
                  className="flex-1 sm:flex-none px-5 py-3 sm:py-2.5 text-sm font-medium btn-outline disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px]"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (!canProceedFromStep(step)) {
                      setShowErrors(true);
                      toast.error('Please complete all required fields before continuing.');
                      return;
                    }
                    setShowErrors(false);
                    goNext();
                  }}
                  className="flex-1 sm:flex-none px-6 py-3 sm:py-2.5 text-sm font-semibold btn-primary flex items-center justify-center gap-2 min-h-[44px]"
                >
                  Continue
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Order summary panel — below on mobile, sidebar on xl+ */}
        <div className="xl:col-span-1 2xl:col-span-1 order-2 xl:order-2">
          <OrderSummaryPanel order={order} currentStep={step} />
        </div>
      </div>
    </div>
  );
}